var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, ButtonGroup, CircularProgress, InputAdornment, TextField } from '@material-ui/core';
import { useDataSourceProps } from '../hooks/form/useDataSourceProps';
import { useFormContext } from '../FormContext';
import DoneIcon from '@material-ui/icons/Done';
import ClearIcon from '@material-ui/icons/Clear';
import styled from 'styled-components';
const StyledTextField = styled(TextField) `
  .select-field {
    margin-right: 22px;
  }
  .MuiInputBase-multiline {
    align-items: flex-end;
  }
  .multiline {
    margin-bottom: 10px;
  }
`;
export const FormTextField = (_a) => {
    var { element, edit, disabled, disableInlineSave, select, children, onChange } = _a, props = __rest(_a, ["element", "edit", "disabled", "disableInlineSave", "select", "children", "onChange"]);
    const { formState, formValues, setFormValue } = useFormContext();
    const key = element.dataSource || element.key;
    const { placeholder, multiline = props.multiline } = element.config || {};
    const [focus, setFocus] = useState(false);
    const [touched, setTouched] = useState(false);
    const [value, setValue] = useState(props.value || formValues[key] || '');
    const onChangeRef = useRef(onChange);
    const previousValueRef = useRef(value);
    const cancelButtonRef = useRef();
    const dataSourceProps = useDataSourceProps(element, value);
    // refresh value if formValues change outside of the component
    useEffect(() => {
        if (previousValueRef.current !== formValues[key]) {
            setValue(formValues[key] || '');
            previousValueRef.current = formValues[key] || '';
        }
    }, [formValues, key]);
    const handleSubmit = useCallback((event) => {
        var _a;
        (_a = onChangeRef.current) === null || _a === void 0 ? void 0 : _a.call(onChangeRef, event, value);
        if (event.isPropagationStopped() || event.isDefaultPrevented()) {
            return;
        }
        setFormValue(key, value);
    }, [key, value, setFormValue]);
    const handleCancel = () => {
        setValue(previousValueRef.current);
    };
    const getAdornmentClasses = useCallback(() => {
        const classes = [];
        if (select)
            classes.push('select-field');
        if (multiline)
            classes.push('multiline');
        return classes.join(' ');
    }, [select, multiline]);
    const inputProps = useMemo(() => {
        if (formState.isSubmitting && !disableInlineSave) {
            return {
                endAdornment: (React.createElement(InputAdornment, { position: 'end', className: getAdornmentClasses() },
                    React.createElement(CircularProgress, { color: 'primary', size: 25 })))
            };
        }
        else if (focus && !disableInlineSave) {
            return {
                endAdornment: (React.createElement(InputAdornment, { position: 'end', className: getAdornmentClasses() },
                    React.createElement(ButtonGroup, { color: 'primary', variant: 'text' },
                        React.createElement(Button, { size: 'small', tabIndex: -1, onClick: handleSubmit },
                            React.createElement(DoneIcon, null)),
                        React.createElement(Button, { size: 'small', tabIndex: -1, innerRef: cancelButtonRef, onClick: event => {
                                event.stopPropagation();
                                handleCancel();
                            } },
                            React.createElement(ClearIcon, null)))))
            };
        }
    }, [
        disableInlineSave,
        focus,
        formState.isSubmitting,
        getAdornmentClasses,
        handleSubmit
    ]);
    return (React.createElement(StyledTextField, { fullWidth: true, select: select, multiline: multiline, disabled: edit || disabled, variant: 'outlined', placeholder: placeholder, value: value, error: dataSourceProps.error, helperText: dataSourceProps.helperText, InputProps: inputProps, onFocus: () => setFocus(true), onChange: event => {
            setValue(event.target.value);
            setTouched(true);
        }, onKeyDown: event => {
            // console.log('event.key', event.key);
            switch (event.key) {
                case 'Escape':
                    return handleCancel();
                // case 'Tab':
                // case 'Enter':
                //   return handleSubmit();
            }
        }, onBlur: event => {
            setFocus(false);
            if (event.relatedTarget === cancelButtonRef.current) {
                handleCancel();
            }
            else {
                if (touched && formValues[key] !== value) {
                    handleSubmit(event);
                }
            }
        } }, children));
};
export default FormTextField;
//# sourceMappingURL=FormTextField.js.map